import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';
import { openNotification, needsDecision } from '../utils/notificationAction';
import { Layer, useOverlay } from '../ui/Overlay';
import { Button, ChipGroup, StatusPill, useDelayedDelete } from '../ui';
import { bellGroups, unreadLabel } from '../utils/notificationState';
import '../ui/shell/shell.css';
import { personalFlag } from '../nav/views';
import { describeWriteFailure } from '../utils/writeFailure';
import { formatTaskDay } from '../utils/taskTime';
import { messageFromError } from '../utils/apiError';
import { showErrorToast } from '../utils/toast';

/**
 * ✅ NOTIFICATION BELL COMPONENT
 * 
 * Features:
 * - Real-time notification count badge
 * - Dropdown panel with notification list
 * - Mark as read functionality
 * - Different notification types with icons
 * - Auto-refresh every 30 seconds
 */

// Tier 1 #7: the bell was built but mounted NOWHERE, so no notification — a
// client's portal message included — could be seen outside the dashboard card.
// It is mounted by the shell (UI-4): variant="sidebar" in the Sidebar (desktop, tablet rail and
// the phone drawer) and variant="topbar" in the phone TopBar.
// SITTING 4.2/4.3 (Carla, on device): the DESKTOP bell did not respond to a
// click — the panel opened where she could not see it. It was `position: fixed`
// inside `.sidebar`, which is itself fixed, scrollable and a stacking context at
// z-index 1000, so the panel's own z-index was resolved INSIDE that context and
// the sidebar's overflow could clip it. It is now rendered through a portal on
// document.body: no ancestor can clip it, overlay it or scroll it away, in any
// browser. The mobile bell worked, which is why this was desktop-only.
//
// `extraCount` is the merge (Carla, 2026-09-22): one sidebar item, one page, ONE
// count. The sidebar used to carry Notifications AND Attention Required as
// separate entries with separate badges.
// UI-4: restyled per brief 4 and decision 54. variant 'sidebar' (the shell's sidebar row) or
// 'topbar' (the phone top bar; 'header' is the old name and still works).
const NotificationBell = ({ setFilter, variant = 'topbar', collapsed = false, extraCount = 0 }) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState([]);
  const [inviteFailure, setInviteFailure] = useState({});
  
  const {
    currentUser,
    notifications,
    notificationCounts,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    clearReadNotifications,
    workspaces,
    activeWorkspaceId,
  } = useCurrentUser();

  // Carla, 2026-10-21 (proven live): after "Mark all as read" the server said unread 0 and the bell
  // still showed 16, because the badge was "needs attention". The badge is UNREAD ONLY: the server's
  // number (lib/notificationCounts, held by the context, the ONE copy every bell reads, so the sidebar
  // and the top bar cannot disagree), and the context sets it to 0 the moment she marks all read.
  // "Needs attention" is the Dashboard card's number and appears nowhere on the bell.
  const unreadCount = notificationCounts ? notificationCounts.unread : null;
  // The email suggestions, for the Needs attention chip's own row: the server's count when it has one.
  const proposalCount = notificationCounts && typeof notificationCounts.proposals === 'number' ? notificationCounts.proposals : (extraCount || 0);
  const bellLabel = unreadLabel(unreadCount);

  // Notifications arrive already workspace-scoped from the server, so no client
  // filter is needed — just mirror the context list.
  useEffect(() => {
    setLocalNotifications(notifications || []);
  }, [notifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }
    setIsOpen(false);

    // The destination logic lives in utils/notificationAction so the merged
    // Notifications page does exactly the same thing (4.2/4.3).
    openNotification(notification, setFilter, personalFlag(workspaces, activeWorkspaceId));
  };

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead();
  };

  const [inviteLoading, setInviteLoading] = useState({});

  const handleInviteAction = async (e, notificationId, action) => {
    e.stopPropagation();
    if (inviteLoading[notificationId]) return;
    setInviteLoading(prev => ({ ...prev, [notificationId]: action }));
    setInviteFailure(prev => ({ ...prev, [notificationId]: undefined }));
    try {
      const endpoint = action === 'accept' ? '/api/calendar/invite-accept' : '/api/calendar/invite-ignore';
      await api(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId }),
      });
      // Optimistically remove from local list (backend dismissed it)
      setLocalNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      // "the notification stays visible so the user can retry" was the old
      // reasoning, and it is not enough: a row that stays put looks the same
      // whether the accept worked or not, and she would believe a meeting was in
      // her calendar. The reason now sits ON THE ROW, under the two buttons.
      setInviteFailure(prev => ({
        ...prev,
        [notificationId]: describeWriteFailure(err, action === 'accept' ? 'add that to your calendar' : 'ignore that invitation'),
      }));
    } finally {
      setInviteLoading(prev => { const next = { ...prev }; delete next[notificationId]; return next; });
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      mention: '💬',
      task_assigned: '📋',
      meeting_invitation: '📅',
      invite_received: '📩',
      payment_issue: '💳',
      invoice_overdue: '🧾',
      subscription_updated: '✅',
      subscription_canceled: '❌',
      usage_warning: '⚠️',
      team_invitation: '👥',
      project_update: '📊',
      deadline_reminder: '⏰',
      aidey_action_pending: '🤖',
      aidey_action_done: '✅',
      aidey_intelligence: '💡',
      leave_request: '🏖️',
      leave_approved: '✅',
      leave_refused: '❌',
      leave_cancelled: '↩️',
    };
    return icons[type] || '🔔';
  };

  // UI-4: the icon tile's pastel, by kind of notice.
  const tileColour = (n) => {
    if (n.priority === 'urgent' || n.priority === 'high') return 'var(--pastel-orange)';
    if (/invoice|payment|expense/.test(n.type || '')) return 'var(--pastel-mint)';
    if (/meeting|invite|booking|event/.test(n.type || '')) return 'var(--pastel-blue)';
    if (/leave|team|task/.test(n.type || '')) return 'var(--pastel-lilac)';
    return 'var(--pastel-neutral)';
  };

  const formatNotificationTime = (createdAt) => {
    const now = new Date();
    const notificationDate = new Date(createdAt);
    const diffMs = now - notificationDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return notificationDate.toLocaleDateString();
  };

  const [tab, setTab] = useState('all');
  const panelRef = useRef(null);

  // UI-4b: the first few event proposals, with Add to calendar and Dismiss right here (the same
  // endpoints as the Notifications page's banner). Loaded when Needs attention is opened.
  const [proposals, setProposals] = useState([]);
  const [proposalBusy, setProposalBusy] = useState(null);
  useEffect(() => {
    if (!isOpen || tab !== 'attention' || !(proposalCount > 0)) return undefined;
    let alive = true;
    api('/api/event-proposals')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => { if (alive) setProposals(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setProposals([]); });   // the summary row and Review still work
    return () => { alive = false; };
  }, [isOpen, tab, proposalCount]);
  const proposalDone = (id) => {
    setProposals((prev) => prev.filter((x) => x.id !== id));
    window.dispatchEvent(new Event('aidey:tasksUpdated'));   // App refreshes the count from this
  };
  const acceptProposal = async (p) => {
    setProposalBusy(p.id);
    try {
      const res = await api(`/api/event-proposals/${p.id}/accept`, { method: 'POST' });
      const data = res.ok ? await res.json() : null;
      // A clash needs the full choice (keep both, move one...), which lives on the page.
      if (data?.requiresResolution) { setIsOpen(false); if (setFilter) setFilter('Notifications'); return; }
      proposalDone(p.id);
    } catch (err) {
      showErrorToast(messageFromError(err, 'Could not add that to your calendar.'));
    } finally { setProposalBusy(null); }
  };
  const dismissProposal = async (p) => {
    setProposalBusy(p.id);
    try {
      await api('/api/event-proposals/dismiss', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: [p.id] }),
      });
      proposalDone(p.id);
    } catch (err) {
      showErrorToast(messageFromError(err, 'Could not dismiss that suggestion.'));
    } finally { setProposalBusy(null); }
  };
  // UI-4: the panel is a real overlay: focus moves in and returns, Escape closes it, the page
  // behind holds still, and FloatingAsk hides while it is open.
  useOverlay(panelRef, isOpen, { onClose: () => setIsOpen(false) });

  // "Clear all" (Carla, 2026-10-21): the read ones under "Earlier" go at once, with Undo, and only
  // after the wait are they dismissed on the server (the house delayed delete). Unread ones are never
  // in the batch: the ids are the ones she was shown under Earlier, and the server dismisses only
  // read ones among them (PUT /api/notifications/clear-read).
  const clearing = useDelayedDelete({
    send: (batch, { keepalive }) => clearReadNotifications(batch.ids, { keepalive }),
    describe: (batch) => `Cleared ${batch.ids.length} read notification${batch.ids.length === 1 ? '' : 's'}.`,
    failMessage: 'Those notifications could not be cleared.',
  });
  const [clearBatches, setClearBatches] = useState([]);
  const clearedIds = new Set(clearBatches.filter((b) => clearing.isHidden(b.id)).flatMap((b) => b.ids));

  if (!currentUser) return null;

  // Decision 54: All, Unread and Needs attention (the same rule as the Notifications page:
  // actionable, high or urgent). Event proposals sit at the top of Needs attention.
  const visible = localNotifications.filter((n) => !clearedIds.has(n.id));
  const attention = visible.filter(needsDecision);
  // All: what is still waiting first, then the read ones faded under "Earlier" (Carla, 2026-10-21).
  const groups = bellGroups(visible);
  const shown = tab === 'unread' ? visible.filter((n) => !n.read)
    : tab === 'attention' ? attention : groups.current;
  const earlier = tab === 'all' ? groups.earlier : [];
  const clearAll = () => {
    const batch = { id: `clear-${Date.now()}`, ids: earlier.map((n) => n.id) };
    if (!batch.ids.length || !clearReadNotifications) return;
    setClearBatches((prev) => [...prev, batch]);
    clearing.remove(batch);
  };
  const goToPage = () => { setIsOpen(false); if (setFilter) setFilter('Notifications'); };

  const earlierIds = new Set(earlier.map((n) => n.id));
  const renderRow = (notification) => (
    <div
      key={notification.id}
      role="button"
      tabIndex={0}
      className={`ui-bellpanel__row${notification.read ? '' : ' ui-bellpanel__row--unread'}${earlierIds.has(notification.id) ? ' ui-bellpanel__row--read' : ''}`}
      onClick={() => handleNotificationClick(notification)}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); handleNotificationClick(notification); } }}
    >
      <span className="ui-bellpanel__tile" style={{ background: tileColour(notification) }} aria-hidden="true">
        {getNotificationIcon(notification.type)}
      </span>
      <div className="ui-bellpanel__content">
        <span className="ui-bellpanel__rowtitle">
          {notification.title}
          {!notification.read && <span className="ui-bellpanel__dot" aria-label="unread" />}
        </span>
        <span className="ui-bellpanel__body">{notification.message}</span>
        <span className="ui-bellpanel__meta">
          {formatNotificationTime(notification.createdAt)}
          {(notification.priority === 'high' || notification.priority === 'urgent') && (
            <StatusPill priority="high">{notification.priority === 'urgent' ? 'Urgent' : 'High'}</StatusPill>
          )}
        </span>

        {/* Add to calendar / Ignore, for an invitation (real endpoints only). */}
        {notification.type === 'invite_received' && (
          <div className="ui-bellpanel__actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <Button variant="confirm" size="sm" disabled={!!inviteLoading[notification.id]}
              onClick={(e) => handleInviteAction(e, notification.id, 'accept')}>
              {inviteLoading[notification.id] === 'accept' ? 'Adding…' : 'Add to calendar'}
            </Button>
            <Button variant="secondary" size="sm" disabled={!!inviteLoading[notification.id]}
              onClick={(e) => handleInviteAction(e, notification.id, 'ignore')}>
              {inviteLoading[notification.id] === 'ignore' ? 'Ignoring…' : 'Ignore'}
            </Button>
          </div>
        )}
        {notification.type === 'invite_received' && inviteFailure[notification.id] && (
          <p data-testid={`invite-failed-${notification.id}`} className="ui-field__error" onClick={(e) => e.stopPropagation()}>
            {inviteFailure[notification.id]}
          </p>
        )}
      </div>
    </div>
  );

  const sidebar = variant === 'sidebar';
  return (
    <div className={`ui-bell ui-bell--${sidebar ? 'sidebar' : 'topbar'}`}>
      <button
        type="button"
        className="ui-bell__button ui-hit"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={bellLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        title="Notifications"
      >
        <span aria-hidden="true">🔔</span>
        {unreadCount > 0 && (
          <span data-testid="bell-badge" className="ui-bell__badge" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* The panel, in a portal on <body> (4.2/4.3: inside the sidebar it was clipped). Beside the
          sidebar on tablet and desktop, at the sidebar's real width; full screen on a phone. */}
      {isOpen && (
        <Layer>
          <div className="ui-bell__backdrop" aria-hidden="true" onClick={() => setIsOpen(false)} />
          <section ref={panelRef} role="dialog" aria-modal="true" aria-label="Notifications" tabIndex={-1} className="ui-bellpanel">
            <div className="ui-bellpanel__band">
              <h2 className="ui-bellpanel__title">
                {t('notifications.title')}
                {unreadCount > 0 && <span className="ui-bellpanel__count">{unreadCount}</span>}
              </h2>
              <div className="ui-bellpanel__tools">
                {unreadCount > 0 && (
                  <Button variant="tertiary" size="sm" onClick={handleMarkAllAsRead}>{t('notifications.mark_all_read')}</Button>
                )}
              </div>
              <button type="button" className="ui-overlay__close ui-hit" aria-label="Close notifications" onClick={() => setIsOpen(false)}>
                <span aria-hidden="true">✕</span>
              </button>
            </div>

            <div className="ui-bellpanel__chips">
              <ChipGroup label="Show" value={tab} onChange={setTab} scroll={false}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'unread', label: unreadCount > 0 ? `Unread ${unreadCount}` : 'Unread' },
                  { value: 'attention', label: 'Needs attention' },
                ]} />
            </div>

            <div className="ui-bellpanel__list">
              {tab === 'attention' && proposalCount > 0 && (
                <div className="ui-bellpanel__row ui-bellpanel__row--proposals" data-testid="bell-proposals">
                  <span className="ui-bellpanel__tile" style={{ background: 'var(--pastel-yellow)' }} aria-hidden="true">📬</span>
                  <div className="ui-bellpanel__content">
                    <span className="ui-bellpanel__rowtitle">
                      Aidey spotted {proposalCount} {proposalCount === 1 ? 'event' : 'events'} in your emails
                    </span>
                    <span className="ui-bellpanel__body">Add them to your calendar or dismiss them here, or see them all on the Notifications page.</span>
                    <span><Button variant="secondary" size="sm" onClick={goToPage}>See all</Button></span>
                  </div>
                </div>
              )}
              {tab === 'attention' && proposalCount > 0 && proposals.slice(0, 3).map((p) => (
                <div key={p.id} className="ui-bellpanel__row" data-testid={`bell-proposal-${p.id}`}>
                  <span className="ui-bellpanel__tile" style={{ background: 'var(--pastel-blue)' }} aria-hidden="true">🗓️</span>
                  <div className="ui-bellpanel__content">
                    <span className="ui-bellpanel__rowtitle">{p.title}</span>
                    <span className="ui-bellpanel__body">{[formatTaskDay(p.event_date), p.event_time].filter(Boolean).join(' · ')}</span>
                    <span className="ui-bellpanel__actions">
                      <Button variant="confirm" size="sm" disabled={proposalBusy === p.id} onClick={() => acceptProposal(p)}>Add to calendar</Button>
                      <Button variant="secondary" size="sm" disabled={proposalBusy === p.id} onClick={() => dismissProposal(p)}>Dismiss</Button>
                    </span>
                  </div>
                </div>
              ))}
              {shown.length === 0 && earlier.length === 0 && !(tab === 'attention' && proposalCount > 0) ? (
                <div className="ui-bellpanel__empty">
                  <span aria-hidden="true">🔕</span>
                  <strong>{tab === 'all' ? t('notifications.empty_title') : 'Nothing here'}</strong>
                  <span>{tab === 'all' ? t('notifications.no_notifications') : "You're all caught up."}</span>
                </div>
              ) : (
                <>
                  {shown.map(renderRow)}
                  {earlier.length > 0 && (
                    <section data-testid="bell-earlier" className="ui-bellpanel__earlier" aria-labelledby="bell-earlier-title">
                      <div className="ui-bellpanel__earlierhead">
                        <h3 id="bell-earlier-title" className="ui-bellpanel__earliertitle">Earlier</h3>
                        <button type="button" className="ui-bellpanel__clear ui-hit" onClick={clearAll}>Clear all</button>
                      </div>
                      {earlier.map(renderRow)}
                    </section>
                  )}
                </>
              )}
            </div>

            {/* Always offered: things needing attention may be waiting on the page even when this
                list is empty (the merge). */}
            <button type="button" className="ui-bellpanel__footer" onClick={goToPage}>
              View all notifications
            </button>
          </section>
        </Layer>
      )}
    </div>
  );
};

export default NotificationBell;
